import { useEffect, useState } from "react";
import type { ArtifactTarget } from "./artifact-open";
import { fetchArtifactBytes } from "./artifact-open";
import { drawVideoPoster } from "./artifact-video-poster";

/** Videos above this size get a neutral tile; the file is not downloaded just for a thumbnail. */
export const VIDEO_POSTER_MAX_BYTES = 32 * 1024 * 1024;

const CACHE_MAX_ENTRIES = 4;
const CACHE_MAX_BYTES = 2 * VIDEO_POSTER_MAX_BYTES;

type CachedBytes = { promise: Promise<Uint8Array>; size: number };

// Oldest first. It holds bytes only; the preview dialog makes its own object URL from them.
const cache = new Map<string, CachedBytes>();

function trimCache() {
  let total = 0;
  for (const entry of cache.values()) total += entry.size;
  for (const [key, entry] of cache) {
    if (cache.size <= CACHE_MAX_ENTRIES && total <= CACHE_MAX_BYTES) return;
    cache.delete(key);
    total -= entry.size;
  }
}

/** Drops every cached video: the files belong to the signed-in person and must not outlive them. */
export function clearVideoBytesCache(): void {
  cache.clear();
}

/**
 * Empties the cache when the signed-in person changes or signs out, including a session that
 * expired. The page is not reloaded then, so the next account in this tab would otherwise be
 * served the previous person's cached bytes.
 */
export function useVideoBytesCacheScope(userId: string | undefined): void {
  useEffect(() => clearVideoBytesCache, [userId]);
}

/**
 * Downloads a video once for the thumbnail and the preview dialog. A video above the thumbnail cap
 * is never cached, so one large file cannot push the small ones out.
 */
export function fetchVideoBytes(
  target: ArtifactTarget,
  artifactId: string,
  size: number,
): Promise<Uint8Array> {
  if (size > VIDEO_POSTER_MAX_BYTES) return fetchArtifactBytes(target, artifactId);
  const key = JSON.stringify([
    "botId" in target ? "bot" : "group",
    "botId" in target ? target.botId : target.groupId,
    artifactId,
  ]);
  const hit = cache.get(key);
  if (hit) {
    cache.delete(key);
    cache.set(key, hit);
    return hit.promise;
  }
  const entry: CachedBytes = {
    size: 0,
    promise: fetchArtifactBytes(target, artifactId).then(
      (bytes) => {
        entry.size = bytes.byteLength;
        if (cache.get(key) === entry) trimCache();
        return bytes;
      },
      (error) => {
        if (cache.get(key) === entry) cache.delete(key);
        throw error;
      },
    ),
  };
  cache.set(key, entry);
  trimCache();
  return entry.promise;
}

/** A first-frame data URL for a video, or null while it is not wanted, loading or undrawable. */
export function useArtifactVideoPoster(
  target: ArtifactTarget,
  artifactId: string,
  size: number,
  enabled: boolean,
) {
  const [poster, setPoster] = useState<string | null>(null);
  const botId = "botId" in target ? target.botId : undefined;
  const groupId = "groupId" in target ? target.groupId : undefined;
  const wanted = enabled && size <= VIDEO_POSTER_MAX_BYTES;

  useEffect(() => {
    setPoster(null);
    const artifactTarget: ArtifactTarget | undefined =
      botId !== undefined ? { botId } : groupId !== undefined ? { groupId } : undefined;
    if (!wanted || !artifactTarget) return;
    const controller = new AbortController();
    void fetchVideoBytes(artifactTarget, artifactId, size)
      .then((bytes) => drawVideoPoster(bytes, controller.signal))
      .then((url) => {
        if (!controller.signal.aborted) setPoster(url);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [artifactId, botId, groupId, size, wanted]);

  return poster;
}
